// Recalibration settings card — preference toggle + "Start now" entry point.
// Lives on the Profile page for paid users.
//
// Source: CogniPrint_Recalibration_Dev_Plan_v1.md §3.11 (REC-050).

import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { RecalibrationPreference } from '@shared/types';
import {
  requestAdHocRecalibration,
  updateRecalibrationPreference,
} from '@shared/services/recalibration-api';
import { useRecalibrationStore } from '@shared/state';

interface Props {
  currentPreference: RecalibrationPreference;
  onStartWizard: () => void;
}

const OPTIONS: Array<{
  value: RecalibrationPreference;
  label: string;
  helper: string;
}> = [
  { value: 'more_often', label: 'More often', helper: '≈ every 3 weeks' },
  { value: 'default', label: 'Default', helper: '≈ every 4 weeks' },
  { value: 'less_often', label: 'Less often', helper: '≈ every 6 weeks' },
];

export function RecalibrationSettings({ currentPreference, onStartWizard }: Props) {
  const schedule = useRecalibrationStore((s) => s.schedule);
  const [preference, setPreference] = useState<RecalibrationPreference>(currentPreference);
  const [saving, setSaving] = useState(false);
  const [savedCycleDays, setSavedCycleDays] = useState<number | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function handlePrefChange(next: RecalibrationPreference) {
    setPreference(next);
    setSaving(true);
    setErrMsg(null);
    try {
      const result = await updateRecalibrationPreference(next);
      setSavedCycleDays(result.cycleDays);
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : 'Could not save preference');
      setPreference(currentPreference);
    } finally {
      setSaving(false);
    }
  }

  async function handleStartNow() {
    setRequesting(true);
    setErrMsg(null);
    setCooldownUntil(null);
    try {
      await requestAdHocRecalibration();
      onStartWizard();
    } catch (err) {
      const raw = err as {
        code?: string;
        message?: string;
        details?: { code?: string; retryAt?: string };
      };
      if (raw.details?.code === 'RECAL_COOLDOWN_ACTIVE' && raw.details.retryAt) {
        setCooldownUntil(raw.details.retryAt);
      } else {
        setErrMsg(raw.message ?? 'Could not start recalibration');
      }
    } finally {
      setRequesting(false);
    }
  }

  return (
    <div
      className="rounded-xl p-5 mt-6"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-[16px] text-white mb-1">
            Recalibration
          </h2>
          <p className="text-content-muted text-[13px] m-0 leading-snug">
            Scheduled check-ins that sharpen your CogniPrint. Your cadence stays
            between 21 and 56 days regardless of preference.
          </p>
        </div>
        {schedule?.nextRecalibration && (
          <div
            className="shrink-0 text-[11px] font-code rounded-md"
            style={{
              padding: '4px 10px',
              background: 'rgba(108,92,231,0.08)',
              border: '1px solid rgba(108,92,231,0.25)',
              color: '#A39BE0',
            }}
          >
            next {new Date(schedule.nextRecalibration).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </div>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-wider text-content-dim font-semibold mb-2">
        Cadence preference
      </div>
      <div className="flex flex-col gap-2 mb-4">
        {OPTIONS.map((opt) => {
          const selected = preference === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => void handlePrefChange(opt.value)}
              disabled={saving}
              className="text-left rounded-lg transition-all duration-200 disabled:opacity-60"
              style={{
                padding: '10px 14px',
                background: selected
                  ? 'rgba(108,92,231,0.14)'
                  : 'rgba(255,255,255,0.02)',
                border: selected
                  ? '1px solid rgba(108,92,231,0.45)'
                  : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div
                className="text-[13px] font-semibold"
                style={{ color: selected ? '#A39BE0' : '#e1dfef' }}
              >
                {opt.label}
              </div>
              <div className="text-[11px] text-content-muted mt-0.5">
                {opt.helper}
              </div>
            </button>
          );
        })}
      </div>
      {savedCycleDays !== null && (
        <div className="text-[11px] text-content-muted mb-4">
          Saved — next cycle scheduled for {savedCycleDays} days from your last recalibration.
        </div>
      )}

      <div
        className="rounded-lg p-3 flex items-center gap-3 mt-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(108,92,231,0.08), rgba(163,155,224,0.03))',
          border: '1px solid rgba(108,92,231,0.22)',
        }}
      >
        <Sparkles size={16} className="text-indigo-300 shrink-0" />
        <div className="flex-1 text-[12px] text-content-secondary leading-snug">
          Start a full recalibration now. Subject to a 21-day cooldown
          (7 days on Coach).
        </div>
        <button
          onClick={handleStartNow}
          disabled={requesting}
          className="shrink-0 rounded-lg text-[12px] font-semibold text-white disabled:opacity-40"
          style={{
            padding: '7px 14px',
            background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
          }}
        >
          {requesting ? 'Checking…' : 'Start now'}
        </button>
      </div>
      {cooldownUntil && (
        <p className="text-[11px] text-gold-500 mt-2">
          Cooldown active — next available{' '}
          {new Date(cooldownUntil).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
          .
        </p>
      )}
      {errMsg && <p className="text-[11px] text-red-400 mt-2">{errMsg}</p>}
    </div>
  );
}
